import { defineStore } from 'pinia'
import type { ISelectItem } from '@/components/ui/select/types'
import type { ILanguages } from '@/utils/constants/locale'
import type { ISlug } from '@/types/Global'
import { LANGUAGE_LIST, NEWSLETTER_LOCALES } from '@/utils/constants/locale'
import { useLocaleStore } from '@/stores/Locale.store'
import { usePageStore } from '@/stores/Page.store'

export const useLanguagesStore = defineStore('languagesStore', () => {
  const localeStore = useLocaleStore()
  const pageStore = usePageStore()

  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()

  const PAGES_WITH_EXTERNAL_LINKS = ['/index']

  const availableLanguages = computed(() => {
    let languages: ILanguages[] = LANGUAGE_LIST[localeStore.selectedCountry]

    const showExternalLinks = PAGES_WITH_EXTERNAL_LINKS.some((link) => route.path.includes(link))

    if (!showExternalLinks) {
      languages = languages.filter((lang: ILanguages) => !lang.external)
    }

    if (pageStore.slugs?.length) {
      const slugProperty = runtimeConfig.public.env === 'production' ? 'published' : 'name'
      const publishedLanguages = new Set(
        pageStore.slugs?.reduce((acc: string[], slug: ISlug) => {
          if (slug[slugProperty]) {
            acc.push(slug.lang)
          }
          return acc
        }, [])
      )
      languages = languages.filter((lang: ILanguages) => publishedLanguages.has(lang.id) || lang.external)
    }

    const isLangAvailable = languages.find((item: ISelectItem) => item.id === localeStore.selectedLanguage)

    if (!isLangAvailable) {
      localeStore.setSelectedLanguage(languages[0].id)
    }

    return languages
  })

  const newsletterLanguages = computed<ILanguages[]>(() => {
    return Object.values(Object.fromEntries(NEWSLETTER_LOCALES.map((language) => [language.id, language])))
  })

  return {
    availableLanguages,
    newsletterLanguages
  }
})
